import { useEffect, useState } from 'react';

/**
 * How long a boot gate may stay unresolved before the app admits it is stuck.
 *
 * 15s is chosen to sit well past a slow-but-working load and well short of the
 * point where someone starts reloading by hand. It is not a request timeout —
 * nothing is cancelled — it is the moment the UI stops pretending progress is
 * being made.
 */
export const STALLED_LOAD_TIMEOUT_MS = 15_000;

/**
 * `true` once `active` has been CONTINUOUSLY true for `timeoutMs` (TV1-2369).
 *
 * ── The failure this exists for ───────────────────────────────────────────
 *
 * The app's boot gates — the auth check, the capabilities query, the
 * feature-flag query — each render a full-screen loader while they resolve, and
 * none of them has an unhappy path. If a query never settles (a dropped socket,
 * a 500 behind a retry, a request that simply never returns) the loader spins
 * forever and the user is left with no error, no explanation and nothing to
 * click. `ErrorBoundary` cannot help: nothing THREW.
 *
 * The timer resets whenever `active` goes false, so a gate that resolves — even
 * slowly — never trips it. What trips it is a gate that stops making progress.
 *
 * Deliberately NOT a request timeout. Cancelling the in-flight query would be a
 * guess about what is wrong; showing a way out is not. If the data arrives at
 * 16 seconds the component below re-renders normally and the page goes away on
 * its own.
 */
export const useStalledAfter = (
  active: boolean,
  timeoutMs: number = STALLED_LOAD_TIMEOUT_MS
): boolean => {
  const [ stalled, setStalled ] = useState( false );

  useEffect(() => {
    if ( !active ) {
      // Resolved (or never started): clear any verdict so a later load starts
      // its own clock rather than inheriting this one's.
      setStalled( false );
      return;
    }

    const timer = setTimeout(() => setStalled( true ), timeoutMs );
    return () => clearTimeout( timer );
  }, [ active, timeoutMs ]);

  return stalled;
};

export default useStalledAfter;
